import React, { useEffect, useMemo, useRef, useState } from 'react';
import posthog from 'posthog-js';
import {
  Header, Breadcrumb, PaymentCard, SupportCard, ST, PAYMENT_LOGOS, countryName, flagEmoji, fmtPrice, fmtBaht,
  useDisplayCurrency, readCountryPref, writeCountryPref, readRecentPhones, saveRecentPhone,
  fxStamp, saveSearchedCountry, useMyCountries, carrierLogo, carrierColors, useLogoManifest,
  track, SERVER_LOCALE,
} from './Chrome';
import { LoginPage, SignupPage, ResetPasswordPage, ConfirmEmailPage, ConfirmEmailChangePage, OrdersPage, WalletPage, DepositTHBPage, DepositUSDPage, NumbersPage, ReceiptPage, EsimTopupPage, SettingsPage, SupportPage, ContactPage, TopMeUpPage } from './Account';
import CheckoutPage from './Checkout';
import TopupPayPage from './TopupPay';
import { TermsPage, PrivacyPage, CookiePolicyPage, SecurityPage, AboutPage, AgentsPage, RefundPolicyPage } from './Legal';
import LuckyPage from './Lucky';
import CountriesPage from './Countries';
import {
  TOPUP_COPY, genericTopupCopy, StepsStrip,
  TOPUP_STEPS, ESIM_STEPS, REGIONS, RegionLanding, TrustStrip,
} from './Landing';
import { PayBox, TrustBadge, FaqSection, GIFT_FAQ, SiteFooterV2 } from './Footers';
import { findBySlug, SLUG_BY_ISO } from '../../lib/countrySlugs';
import { slugifyCarrierName } from './carrierSlug';
import MarketHome from './MarketHome';
import { marketForPath } from './markets';
import { DIAL_CODES } from './dialCodes';
import { PlanDetailModal, durationLabel, exitCountries, exitNames, DAY_PRESETS } from './PlanModal';
import { DEFAULT_HERO, PINNED_HEROES, heroStyle, resolveHero } from './hero';
import EsimLanding from './EsimLanding';
import { RequestProductLink } from './RequestProduct';
import { ReferralsPage, InviteBanner } from './Referrals';
import { VoiceButton, VOICE_HINTS } from './VoiceSearch';

const PINNED = [
  { code: 'TH', flag: '🇹🇭' },
  { code: 'PH', flag: '🇵🇭' },
  { code: 'JP', flag: '🇯🇵' },
  { code: 'VN', flag: '🇻🇳' },
  { code: 'MY', flag: '🇲🇾' },
  { code: 'SG', flag: '🇸🇬' },
].map((p) => ({ ...p, hero: PINNED_HEROES[p.code] !== undefined ? PINNED_HEROES[p.code] : DEFAULT_HERO }));
const PINNED_SEARCHES = [
  { search: 'Asia', name: 'Asia · multi', flag: '🌏' },
  { search: 'Global', name: 'Global · multi', flag: '🌐' },
];

// Where the plan's traffic reaches the internet — what IP geolocation shows.
function ExitCell({ v }) {
  const parts = exitCountries(v);
  if (!parts.length) return '—';
  return (
    <span title={`Internet breakout — websites see an IP in ${exitNames(v)}`}>
      {parts.map((c) => (/^[A-Z]{2}$/.test(c) ? `${flagEmoji(c)} ${c}` : c)).join(' / ')}
    </span>
  );
}

// "AIS 5G · DTAC 4G" for the selected country (list feed), or '—'
function networksLabel(p) {
  if (!p.networks || !p.networks.length) return '—';
  return [...new Set(p.networks.map((n) => `${n.name}${n.type ? ` ${n.type}` : ''}`))].join(' · ');
}


function EsimHome({ me, currency, onCurrency, landing, myCountries }) {
  const [countries, setCountries] = useState([]);
  const [query, setQuery] = useState(() => {
    if (landing) return { country: landing.iso, name: landing.name };
    const pref = readCountryPref();
    if (pref) return { country: pref, name: countryName(pref) };
    return { country: 'TH', name: 'Thailand' };
  });
  const [heroUrl, setHeroUrl] = useState(DEFAULT_HERO);
  const [products, setProducts] = useState(null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState('');
  const [segment, setSegment] = useState('all');
  const [sort, setSort] = useState({ key: 'price', dir: 1 });

  function toggleSort(key) {
    setSort((sv) => (sv.key === key ? { key, dir: -sv.dir } : { key, dir: 1 }));
  }
  const [pickerOpen, setPickerOpen] = useState(false);
  const [detail, setDetail] = useState(null); // { p, full, error }
  const [searching, setSearching] = useState(false);
  const [voiceState, setVoiceState] = useState('idle'); // idle | listening | denied | nospeech | error | nomatch
  const searchRef = useRef(null);

  // Guests buy without an account — /checkout collects the email. Daily plans
  // need their length picked first, so they go via the detail modal.
  function startBuy(p, days) {
    if (p.is_daily && days == null) {
      openDetail(p);
      return;
    }
    const qs = new URLSearchParams({ type: 'esim', code: p.slug });
    if (p.is_daily) qs.set('days', String(days));
    window.location.href = `/checkout?${qs.toString()}`;
  }

  function openDetail(p) {
    setDetail({ p, full: null, error: false });
    track('plan_view', { country: query.country, product_type: 'esim', carrier: p.operator });
    fetch(`/api/esims/product/${encodeURIComponent(p.slug)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        setDetail((d) => (d && d.p.slug === p.slug ? { ...d, full: data } : d));
      })
      .catch(() => {
        setDetail((d) => (d && d.p.slug === p.slug ? { ...d, error: true } : d));
      });
  }

  // Country list from the Neon-backed locations feed (via partners API)
  useEffect(() => {
    fetch('/api/esims/locations')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        const list = data.locations
          .map((c) => ({ ...c, name: countryName(c.code), flag: flagEmoji(c.code) }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setCountries(list);
      })
      .catch(() => { /* dropdown degrades to pinned chips */ });
  }, []);

  // The destination shown here carries to /recharge and /gifts
  useEffect(() => { if (query.country) writeCountryPref(query.country); }, [query.country]);

  // Plans for the selected destination
  useEffect(() => {
    let dead = false;
    setProducts(null);
    setError(false);
    const qs = query.country ? `country=${query.country}` : `search=${encodeURIComponent(query.search)}`;
    fetch(`/api/esims/catalog?${qs}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => { if (!dead) setProducts(data.products); })
      .catch(() => { if (!dead) setError(true); });
    return () => { dead = true; };
  }, [query]);

  // Destination hero image
  useEffect(() => {
    const pinned = query.country && PINNED.find((p) => p.code === query.country);
    if (pinned) return setHeroUrl(pinned.hero);
    if (!query.country) return setHeroUrl(DEFAULT_HERO);
    let dead = false;
    resolveHero(query.name, (url) => { if (!dead) setHeroUrl(url); }, query.country);
    return () => { dead = true; };
  }, [query]);

  function pickCountry(code, name) {
    // From an eSIM landing page, switching country moves to that country's
    // own landing URL — keeps the SEO structure and one page per country.
    if (landing && code !== query.country && SLUG_BY_ISO[code]) {
      window.location.href = `/esim/${SLUG_BY_ISO[code]}`;
      return;
    }
    setFilter('');
    setSegment('all');
    setPickerOpen(false);
    writeCountryPref(code);
    saveSearchedCountry(code);
    track('country_view', { country: code, product_type: 'esim' });
    setQuery({ country: code, name: name || countryName(code) });
  }

  function pickSearch(search, name) {
    setFilter('');
    setSegment('all');
    setPickerOpen(false);
    track('search', { query: search, product_type: 'esim' });
    setQuery({ search, name });
  }

  const suggestions = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f || f.length < 2 || !countries.length) return [];
    return countries
      .filter((c) => c.name.toLowerCase().includes(f) || c.code.toLowerCase() === f)
      .sort((a, b) => {
        const ap = a.name.toLowerCase().startsWith(f) ? 0 : 1;
        const bp = b.name.toLowerCase().startsWith(f) ? 0 : 1;
        return ap - bp || a.name.localeCompare(b.name);
      })
      .slice(0, 6);
  }, [filter, countries]);

  // A country name or ISO code stays local. Anything else — "att top up",
  // "$20 apple gift card", "เติมเงิน AIS 100 บาท" — goes through the
  // plain-language parser (POST /api/search/parse), which sends the customer
  // to checkout when it has enough context, else to the right selection
  // page. Any failure falls back to the free-text eSIM search as before.
  async function runSearch(rawText, source) {
    const text = String(rawText || '').trim();
    if (!text || searching) return;
    setVoiceState('idle');
    const sug = suggestions.length ? suggestions[0] : null;
    if (sug && source !== 'voice') return pickCountry(sug.code, sug.name);
    const asCode = countries.find((c) => c.code === text.toUpperCase());
    if (asCode) return pickCountry(asCode.code, asCode.name);
    setSearching(true);
    try {
      const r = await fetch('/api/search/parse', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, source }),
      });
      const d = r.ok ? await r.json() : null;
      const res = d && d.status === 'ok' ? d.resolution : null;
      if (res && (res.action === 'checkout' || res.action === 'page') && res.url) {
        track('search', { query: text, source, product_type: (d.parsed && d.parsed.intent) || 'unknown', action: res.action, reason: res.reason });
        window.location.href = res.url;
        return;
      }
      if (res && res.action === 'search' && res.query) {
        setSearching(false);
        return pickSearch(res.query, text);
      }
      if (res && res.action === 'none' && source === 'voice') {
        // Spoken but not a product request: say so instead of silently
        // running an eSIM text search on "what's the weather".
        setSearching(false);
        setVoiceState('nomatch');
        track('search', { query: text, source, product_type: 'unknown', action: 'none', reason: res.reason });
        return;
      }
    } catch (err) { /* fall through to the plain text search */ }
    setSearching(false);
    // A spoken country name is the one thing the parser hands back as
    // 'search' or nothing useful — local suggestions still catch it here.
    if (sug) return pickCountry(sug.code, sug.name);
    pickSearch(text, text);
  }

  function submitSearch(e) {
    e.preventDefault();
    return runSearch(filter, 'text');
  }

  const voiceHint = voiceState === 'nomatch'
    ? `Didn’t catch a product in “${filter}”. Try “AIS 100 baht to 0812345678” or “Japan eSIM 5 days”.`
    : (VOICE_HINTS[voiceState] || null);

  const shown = useMemo(() => {
    if (!products) return { single: [], regional: [], bestCode: null };
    let list = products;
    const f = filter.trim().toLowerCase();
    if (f.length >= 2 && !suggestions.length) list = list.filter((p) => (p.name || '').toLowerCase().includes(f));

    const base = {
      name: (a, b) => (a.name || '').localeCompare(b.name || ''),
      data: (a, b) => (a.data_bytes ?? 0) - (b.data_bytes ?? 0),
      validity: (a, b) => (a.duration ?? 0) - (b.duration ?? 0),
      price: (a, b) => a.price - b.price,
      pergb: (a, b) => (a.per_gb ?? Infinity) - (b.per_gb ?? Infinity),
    }[sort.key];
    const cmp = (a, b) => sort.dir * base(a, b);

    const singleAll = list.filter((p) => !p.is_regional).sort(cmp);
    const regionalAll = list.filter((p) => p.is_regional).sort(cmp);
    const singlePkg = singleAll.filter((p) => !p.is_daily);
    const singleDaily = singleAll.filter((p) => p.is_daily);
    const regionalPkg = regionalAll.filter((p) => !p.is_daily);
    const regionalDaily = regionalAll.filter((p) => p.is_daily);
    const withGb = singlePkg.filter((p) => p.per_gb != null);
    const bestCode = withGb.length
      ? withGb.reduce((m, p) => (p.per_gb < m.per_gb ? p : m)).slug
      : null;
    const count = singlePkg.length + singleDaily.length + regionalPkg.length + regionalDaily.length;
    return { singlePkg, singleDaily, regionalPkg, regionalDaily, bestCode, count };
  }, [products, filter, sort, suggestions]);

  const showSingle = segment === 'all' && query.country != null;
  // Signed-in visitors see their own countries first; defaults fill the rest
  const pinnedChips = useMemo(() => {
    const base = (myCountries || []).map((code) => ({ code, flag: flagEmoji(code), hero: DEFAULT_HERO }));
    for (const p of PINNED) {
      if (base.length >= 8) break;
      if (!base.some((b) => b.code === p.code)) base.push(p);
    }
    return base.map((p) => {
      const c = countries.find((x) => x.code === p.code);
      return { ...p, name: c ? c.name : countryName(p.code) };
    });
  }, [myCountries, countries]);

  return (
    <div className="page">
      <Header me={me} active="esim" currency={currency} onCurrency={onCurrency} />

      <section className="hero" style={heroStyle(heroUrl)}>
        <Breadcrumb active="esim" iso={query.country} countryName={query.country ? query.name : null}
          onCountryClick={() => setPickerOpen((o) => !o)} />
        <h1 className="hero-title">
          eSIM data {query.country ? 'in' : 'for'} <b>{query.name}</b>
          <span className="picker-anchor">
            <button type="button" className="change" aria-label="Change country" onClick={() => setPickerOpen((o) => !o)}>
              <span className="change-txt">Change country </span>▾
            </button>
            {pickerOpen && (
              <CountryPicker
                countries={countries}
                current={query.country}
                onPick={pickCountry}
                onClose={() => setPickerOpen(false)}
                popular={['TH', 'PH', 'JP', 'VN', 'SG']}
              />
            )}
          </span>
        </h1>
        <form className="finder" onSubmit={submitSearch}>
          <div className="search-wrap">
            <div className="search">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="6" cy="6" r="4.6" stroke="currentColor" strokeWidth="1.6" /><path d="M9.5 9.5 13 13" stroke="currentColor" strokeWidth="1.6" /></svg>
              <input
                ref={searchRef}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search — “japan”, “att top up”, “$20 apple gift card”…"
                aria-label="Search destinations and products"
                aria-busy={searching}
                disabled={searching}
              />
              <VoiceButton
                disabled={searching}
                onState={setVoiceState}
                onInterim={(text) => setFilter(text)}
                onTranscript={(text) => { setFilter(text); runSearch(text, 'voice'); }}
              />
            </div>
            {voiceHint && <div className={`voice-hint ${voiceState}`} role="status">{voiceHint}</div>}
            {suggestions.length > 0 && (
              <div className="suggest">
                {suggestions.map((c) => (
                  <button type="button" key={c.code} onClick={() => pickCountry(c.code, c.name)}>
                    <span className="flag">{c.flag}</span>
                    <span className="sname">{c.name}</span>
                    <span className="smeta">{c.plan_count} plans · from {fmtPrice(c.from_price)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="seg" role="tablist">
            <button type="button" className={segment === 'all' ? 'on' : ''} onClick={() => setSegment('all')}>Single country</button>
            <button type="button" className={segment === 'multi' ? 'on' : ''} onClick={() => setSegment('multi')}>Regional &amp; multi</button>
          </div>
        </form>
        <div className="chips">
          {pinnedChips.map((d) => (
            <button
              type="button"
              key={d.code}
              className={`chip onhero ${query.country === d.code ? 'active' : ''}`}
              onClick={() => pickCountry(d.code, d.name)}
            >
              {d.flag} {d.name}
            </button>
          ))}
          {PINNED_SEARCHES.map((d) => (
            <button
              type="button"
              key={d.name}
              className={`chip onhero ${query.name === d.name ? 'active' : ''}`}
              onClick={() => pickSearch(d.search, d.name)}
            >
              {d.flag} {d.name}
            </button>
          ))}
        </div>
        {landing && landing.sub && <p className="hero-sub">{landing.sub}</p>}
      </section>

      {landing && <StepsStrip steps={ESIM_STEPS} />}
      <InviteBanner me={me} />

      <div className="body">
        <main className="main">
          {/* Mobile gets the one-line cards-summary instead */}
          <div className="sortrow hide-mobile">
            <span className="count">
              {products ? `${shown.count} plans for ` : 'Loading plans for '}
              <b>{query.name}</b>
            </span>
            <span className="paynote">Prices in {currency || 'USD'} · pay in THB at checkout</span>
          </div>

          {error && (
            <div className="notice">
              Couldn’t load plans. <button type="button" className="linkish" onClick={() => setQuery({ ...query })}>Try again</button>
            </div>
          )}

          {!error && (
            <>
            <div className="table-scroll hide-mobile">
              <table className="plans">
                <thead>
                  <tr>
                    <SortTh label="Plan" k="name" sort={sort} onSort={toggleSort} />
                    <SortTh label="Data" k="data" sort={sort} onSort={toggleSort} />
                    <SortTh label="Validity" k="validity" sort={sort} onSort={toggleSort} />
                    <th>Network</th>
                    <th title="Where the plan's traffic reaches the internet — websites see an IP located there">Data exit</th>
                    <SortTh label="Price" k="price" sort={sort} onSort={toggleSort} num />
                    <SortTh label="$/GB" k="pergb" sort={sort} onSort={toggleSort} num />
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {products == null && [1, 2, 3, 4, 5].map((i) => (
                    <tr key={i} className="skeleton"><td colSpan="8"><span /></td></tr>
                  ))}
                  {products != null && showSingle && shown.singlePkg.length > 0 && (
                    <tr className="grp"><td colSpan="8">{query.name} — data packages</td></tr>
                  )}
                  {products != null && showSingle && shown.singlePkg.map((p) => (
                    <PlanRow key={p.slug} p={p} best={p.slug === shown.bestCode} onOpen={openDetail} onBuy={startBuy} />
                  ))}
                  {products != null && showSingle && shown.singleDaily.length > 0 && (
                    <tr className="grp"><td colSpan="8">{query.name} — daily plans <span className="grp-note">data resets each day · choose how many days at checkout</span></td></tr>
                  )}
                  {products != null && showSingle && shown.singleDaily.map((p) => (
                    <PlanRow key={p.slug} p={p} best={false} onOpen={openDetail} onBuy={startBuy} />
                  ))}
                  {products != null && shown.regionalPkg.length > 0 && (
                    <tr className="grp"><td colSpan="8">{query.country ? `Also covers ${query.name} — regional packages` : 'Regional & multi-country packages'}</td></tr>
                  )}
                  {products != null && shown.regionalPkg.map((p) => (
                    <PlanRow key={p.slug} p={p} best={false} onOpen={openDetail} onBuy={startBuy} />
                  ))}
                  {products != null && shown.regionalDaily.length > 0 && (
                    <tr className="grp"><td colSpan="8">{query.country ? `Also covers ${query.name} — regional daily plans` : 'Regional daily plans'}</td></tr>
                  )}
                  {products != null && shown.regionalDaily.map((p) => (
                    <PlanRow key={p.slug} p={p} best={false} onOpen={openDetail} onBuy={startBuy} />
                  ))}
                  {products != null && shown.count === 0 && (
                    <tr><td colSpan="8" className="empty">No plans found{filter ? ` matching “${filter}”` : ''}.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <EsimPlanCards
              products={products} shown={shown} showSingle={showSingle}
              query={query} filter={filter} currency={currency} onOpen={openDetail}
            />
            </>
          )}

          {landing && <FaqSection faq={landing.faq} seo={landing.seo} />}
        </main>

        <aside className="rail">
          <PaymentCard currency={currency} />
          <div className="railcard trust">
            <h4><a href="/refund-policy" style={{ color: 'inherit', textDecoration: 'none' }}>Refund policy</a></h4>
            <b>Automatic refunds for unused data.</b> If your eSIM hasn’t used any data,
            you get a full refund. Failed deliveries are always refunded automatically.
            {' '}<a href="/refund-policy" style={{ fontSize: '0.9em' }}>Full policy</a>
          </div>
          <div className="railcard trust">
            <h4>eSIM topups</h4>
            Plans marked <span className="tag good" style={{ marginLeft: 0 }}>Topup later</span> can
            be recharged with more data anytime — no new QR code, no reinstall. Open any plan
            to see if it supports topups.
          </div>
          {landing && SLUG_BY_ISO[landing.iso] && (
            <div className="railcard">
              <h4>Also available</h4>
              <a href={`/topup/${SLUG_BY_ISO[landing.iso]}`}>Mobile topup in {landing.name}</a> — send
              prepaid credit to any {landing.name} number in seconds.
            </div>
          )}
        </aside>
      </div>

      <SiteFooterV2 currency={currency} onCurrency={onCurrency} />

      {detail && <PlanDetailModal detail={detail} onClose={() => setDetail(null)} onBuy={startBuy} />}
    </div>
  );
}

function SortTh({ label, k, sort, onSort, num }) {
  const active = sort.key === k;
  return (
    <th
      className={`${num ? 'num ' : ''}sortable${active ? ' sorted' : ''}`}
      onClick={() => onSort(k)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onSort(k); }}
      aria-sort={active ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}
    >
      {label}<span className="arrow">{active ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}</span>
    </th>
  );
}

// Matches the CSS card breakpoint — where tap-first layouts replace tables.
function isMobileViewport() {
  return typeof window !== 'undefined' && window.matchMedia('(max-width: 700px)').matches;
}

// Calling code for an ISO country, for countries the Ding list doesn't cover
// (AF, VE, YE, JP...). null if the code isn't a known country.
function dialFor(iso) {
  return DIAL_CODES[iso] || null;
}

// Local number in the selected country's format -> E.164, or null if it's
// too short to be a phone number at all, or local with no known dial code
// (never guess +66 for another country), or a Thai number that isn't
// +66 + a 9-digit mobile (an extra digit used to be cut off server-side, #345).
function toE164(rawPhone, dial) {
  const e164 = toE164Raw(rawPhone, dial);
  if (e164 && e164.startsWith('+66') && !/^\+66[689]\d{8}$/.test(e164)) return null;
  return e164;
}

function toE164Raw(rawPhone, dial) {
  const digits = String(rawPhone || '').replace(/[^\d+]/g, '').replace(/(?!^)\+/g, '');
  if (digits.replace(/\D/g, '').length < 6) return null;
  if (digits.startsWith('+')) return digits;
  const d = dial;
  if (!d) return null;
  if (digits.startsWith('0')) return `+${d}${digits.slice(1)}`;
  if (digits.startsWith(d)) return `+${digits}`;
  return `+${d}${digits}`;
}


///////////////////////////////////////////////////////////////////////////////
// Product-first entry: tapping Topup on a product with no number entered yet
// opens this modal — the chosen amount with eSIM-style detail, a phone field
// (recent numbers one tap away), and the go-to-checkout button.
///////////////////////////////////////////////////////////////////////////////
function TopupDetailModal({ group, product, value, country, initialPhone, onClose, onConfirm }) {
  const [phone, setPhone] = useState(initialPhone || '');
  const [hint, setHint] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 60);
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const amt = product.amounts ? product.amounts.find((x) => x.value === value) : null;
  const price = amt ? amt.price : product.price;
  const receive = amt ? amt.receive : product.receive;
  const recent = readRecentPhones().slice(0, 3);

  function confirm(e) {
    e.preventDefault();
    const e164 = toE164(phone, country.dial);
    if (!e164) {
      setHint(true);
      if (inputRef.current) inputRef.current.focus();
      return;
    }
    onConfirm(e164);
  }

  return (
    <div className="modal-backdrop sheet-top" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal prod-modal" role="dialog" aria-modal="true" aria-label={`${group.name} topup`}>
        <div className="modal-head">
          <div>
            <div className="modal-title">
              {group.logo && <img className="carrier-logo" src={group.logo} alt="" />}
              {group.name}
            </div>
            <div className="modal-chips">
              {receive && <span className="mchip">You get {receive}</span>}
              <span className="mchip">{countryName(country.code)}</span>
            </div>
          </div>
          <div className="modal-price"><b>{price}</b></div>
          <button type="button" className="modal-x" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body">
          <form onSubmit={confirm}>
            <h5>Number to top up</h5>
            <div className="phone-input big" style={{ marginBottom: 8 }}>
              <span className="cc">
                {country.code && /^[A-Z]{2}$/.test(country.code) ? flagEmoji(country.code) : '🌐'} +{country.dial || 'XX'}
              </span>
              <input
                ref={inputRef}
                className="num" type="tel" value={phone}
                onChange={(e) => { setPhone(e.target.value); setHint(false); }}
                placeholder="Phone number"
                aria-label="Phone number to top up"
              />
            </div>
            {recent.length > 0 && (
              <div className="carrier-chips" style={{ marginBottom: 6 }}>
                {recent.map((n) => (
                  <button type="button" key={n} className="chip" onClick={() => { setPhone(n); setHint(false); }}>{n}</button>
                ))}
              </div>
            )}
            {hint && <div className="cp-hint">Enter the full, correct phone number first</div>}
          </form>

          <section style={{ marginTop: 12 }}>
            <h5>Good to know</h5>
            <ul className="facts">
              <li><b>Delivery:</b> credit is applied to the number within seconds of payment, 24/7.</li>
              <li><b>You receive:</b> {receive || price} — face value; any card fee is shown before you pay.</li>
              <li><b>No account needed:</b> the receipt arrives by email.</li>
            </ul>
          </section>

          <div className="modal-pay">
            Pay with card, Thai Banks, Alipay, PayPal, or your balance.
            <div className="paylogos">
              {PAYMENT_LOGOS.map((l) => (
                <img key={l.file} src={`${ST}/images/payment-icons/${l.file}`} alt={l.name} title={l.name} />
              ))}
            </div>
          </div>
        </div>

        <div className="modal-foot">
          <button type="button" className="linkish" onClick={onClose}>Close</button>
          <button type="button" className="btn" style={{ padding: '9px 28px' }} onClick={confirm}>
            Topup · {price}
          </button>
        </div>
      </div>
    </div>
  );
}

///////////////////////////////////////////////////////////////////////////////
// Minimal product-options modal: after the visitor enters a number we detect
// the carrier and offer its amounts right here — no detour via the legacy
// landing. Tapping an amount goes straight to checkout.
///////////////////////////////////////////////////////////////////////////////
function ProductOptionsModal({ group, e164, onClose, onPick, allGroups, onChangeGroup }) {
  const closeRef = useRef(null);
  const [customValue, setCustomValue] = useState('');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // "Pick any amount" products have no preset amounts; they get a typed-amount form.
  const rangeProducts = group.products.filter((prod) => prod.isRange);
  function submitCustom(e, prod) {
    e.preventDefault();
    const v = parseFloat(customValue);
    if (!Number.isFinite(v) || v < (prod.min || 0) || v > (prod.max || Infinity)) return;
    onPick(prod, Math.round(v * 100) / 100);
  }

  const tiles = group.products.filter((prod) => !prod.isRange).flatMap((prod) => {
    const rows = prod.amounts || [null];
    return rows.map((a) => ({
      key: a ? `${prod.sku || prod.operatorId || prod.wonddCarrier}-${a.value}` : String(prod.sku || prod.operatorId || prod.wonddCarrier),
      price: a ? a.price : prod.price,
      receive: a ? a.receive : prod.receive,
      pick: () => onPick(prod, a && a.value),
    }));
  });

  const otherGroups = allGroups && allGroups.filter((g) => g.code !== group.code);

  return (
    <div className="modal-backdrop sheet-top" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal prod-modal" role="dialog" aria-modal="true" aria-label={`Topup options for ${e164}`}>
        <div className="modal-head">
          <div>
            <div className="modal-title">
              {group.logo && <img className="carrier-logo" src={group.logo} alt="" />}
              {group.name}
            </div>
            <div className="pm-sub">{e164}</div>
          </div>
          <button type="button" className="modal-x" onClick={onClose} ref={closeRef} aria-label="Close">✕</button>
        </div>
        <div className="modal-body">
          {rangeProducts.map((prod) => (
            <form className="amt-custom-form" key={prod.operatorId} onSubmit={(e) => submitCustom(e, prod)}>
              <label>
                Enter an amount ({fmtPrice(prod.min || 0)} – {fmtPrice(prod.max || 0)})
                <input type="number" inputMode="decimal"
                  min={prod.min || 0} max={prod.max || undefined} step="0.01"
                  value={customValue} onChange={(e) => setCustomValue(e.target.value)}
                  placeholder={`e.g. ${prod.max || 50}`} />
              </label>
              <div className="amt-custom-actions">
                <button type="submit" className="btn small">Continue</button>
              </div>
            </form>
          ))}
          {tiles.length > 0
            ? (
              <div className="amount-tiles">
                {tiles.map((t) => (
                  <button type="button" key={t.key} className="amount-tile" onClick={t.pick}>
                    <span className="usd">{t.price}</span>
                    {t.receive && <span className="local">{t.receive}</span>}
                  </button>
                ))}
              </div>
            )
            : rangeProducts.length === 0 && <div className="notice">No products for this carrier right now.</div>}
          <div className="cp-note">Pick an amount to check out — no account needed.</div>
          {otherGroups && otherGroups.length > 0 && (
            <div className="change-carrier">
              Wrong carrier?{' '}
              {otherGroups.map((g, i) => (
                <span key={g.code}>
                  {i > 0 && ' · '}
                  <button type="button" className="link-btn" onClick={() => onChangeGroup(g)}>
                    {g.name}
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// The number was submitted but no carrier on this page matched the lookup
// (or nothing was detected). Ask, instead of silently scrolling the grid
// into view (#298). Picking a carrier opens the usual amounts modal.
function CarrierPickModal({ e164, groups, onPick, onClose }) {
  const closeRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // A provider that sells airtime and bundles is two groups with one name;
  // show it once and open its first group (the amounts modal offers the rest).
  const seen = new Set();
  const choices = groups.filter((g) => g.products.length && !seen.has(g.name) && seen.add(g.name));

  return (
    <div className="modal-backdrop sheet-top" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal prod-modal" role="dialog" aria-modal="true" aria-label={`Choose the carrier for ${e164}`}>
        <div className="modal-head">
          <div>
            <div className="modal-title">Which carrier is this number on?</div>
            <div className="pm-sub">{e164}</div>
          </div>
          <button type="button" className="modal-x" onClick={onClose} ref={closeRef} aria-label="Close">✕</button>
        </div>
        <div className="modal-body">
          <div className="notice">We couldn’t detect the carrier for this number — pick it below to see the amounts.</div>
          <div className="pick-carriers">
            {choices.map((g) => (
              <button type="button" key={g.code} className="pick-carrier" onClick={() => onPick(g)}>
                <span className="pick-carrier-logo">
                  {g.logo
                    ? <img src={g.logo} alt="" loading="lazy" />
                    : <span className="ctile-ph">{(g.name || '?').slice(0, 2)}</span>}
                </span>
                <span>{g.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Amounts offered for "choose your own value" products, so the customer picks
// here rather than at checkout. Mirrors dingRangeAmounts in config-shared.
const RANGE_PRESETS = [5, 10, 15, 20, 25, 30, 35, 40, 50];

// Ding promises the smaller of the tax-inclusive / tax-exclusive value.
function netReceive(bound) {
  if (!bound) return null;
  const v = bound.ReceiveValue;
  return (bound.ReceiveValueExcludingTax > 0 && bound.ReceiveValueExcludingTax < v)
    ? bound.ReceiveValueExcludingTax
    : v;
}

// Rate at the middle of a flexible product's range — same formula the quote
// endpoint uses server-side, so the row and the checkout total agree.
function rangeRate(min, max) {
  const lo = netReceive(min);
  const hi = netReceive(max);
  if (lo == null || hi == null) return null;
  if (min.ReceiveCurrencyIso === 'JMD') return lo / min.SendValue;
  return ((lo / min.SendValue) + (hi / max.SendValue)) / 2;
}

// A Reloadly operator becomes one carrier group with a row per denomination.
// Many currencies (MMK, IDR, VND, JPY, KRW...) have no practical subunit —
// showing ".00"/".81" on them is wrong regardless of how precise the
// upstream face value is. Intl's currency-digit defaults turned out to
// differ between Node and the browser's ICU data, so don't rely on them —
// use the same magnitude-based rounding fmtPrice already uses for THB/PHP.
function fmtLocalAmount(v, iso) {
  const n = Number(v);
  if (!Number.isFinite(n)) return `${v} ${iso}`;
  const formatted = Math.abs(n) >= 100 ? Math.round(n).toLocaleString() : n.toFixed(2);
  return `${iso} ${formatted}`;
}

// Face value USD; the service fee appears on the checkout quote, like Ding.
function intlOperatorGroup(op) {
  const cleanName = String(op.name || '')
    .replace(/\s+(USA|England|Scotland|Wales|United Kingdom|Philippines|Canada|Mexico)$/i, '')
    .trim();
  const iso = op.destination_currency || '';
  const showLocal = iso && iso !== 'USD';
  let amounts;
  let rangeProduct = null;
  if (op.denomination_type === 'RANGE') {
    // A "pick any amount" product — one tile, not a wall of preset amounts.
    // The customer types the exact value when they open it.
    rangeProduct = {
      operatorId: op.operator_id,
      isRange: true,
      min: op.min_usd,
      max: op.max_usd,
      iso: showLocal ? iso : null,
      fxRate: op.fx_rate || null,
    };
  } else {
    amounts = (op.amounts || []).map((a) => {
      // Reloadly repeats the operator name in description for plain top-ups
      // ("AT&T USA"), which says nothing about what lands on the phone. Use it
      // only when it actually describes the product (bundles, data packs).
      const value = showLocal && a.local_amount != null
        ? fmtLocalAmount(a.local_amount, iso)
        : fmtPrice(Number(a.amount_usd));
      const desc = String(a.description || '').trim();
      const useful = desc && desc.toLowerCase() !== String(op.name || '').trim().toLowerCase();
      return ({
      value: a.amount_usd,
      receive: useful ? desc : value,
      price: fmtPrice(Number(a.amount_usd)),
      popular: op.most_popular_amount === a.amount_usd,
    });
    });
  }
  return {
    code: `rl-${op.operator_id}`,
    name: cleanName,
    // Ding provider code (null for other suppliers): the phone lookup's
    // exact match, independent of how either side spells the name (#298).
    providerCode: op.provider_code || null,
    logo: carrierLogo(cleanName, op.logo_url),
    intl: true,
    category: op.category || 'airtime',
    // One pseudo-product per operator; the render flattens its amounts into
    // rows exactly like a flexible Ding product. A RANGE operator instead
    // gets a single custom-amount product — see rangeProduct above.
    products: rangeProduct ? [rangeProduct] : (amounts.length ? [{ operatorId: op.operator_id, isRange: false, amounts }] : []),
  };
}

function dingProductRow(p) {
  const min = p.Minimum || {};
  const max = p.Maximum || {};
  const isRange = min.SendValue != null && max.SendValue != null && max.SendValue > min.SendValue;
  const iso = min.ReceiveCurrencyIso || '';
  const receive = (v) => (v != null ? `${v} ${iso}`.trim() : '—');

  // A flexible product becomes one row per amount — the ends of the range
  // plus every preset in between.
  let amounts = null;
  if (isRange) {
    const rate = rangeRate(min, max);
    const values = [...new Set([
      min.SendValue,
      ...RANGE_PRESETS.filter((v) => v > min.SendValue && v < max.SendValue),
      max.SendValue,
    ])].sort((a, b) => a - b);
    amounts = values.map((v) => ({
      value: v,
      receive: rate != null ? receive(Math.round(v * rate * 100) / 100) : '—',
      price: fmtPrice(Number(v)),
    }));
  }

  return {
    sku: p.SkuCode,
    provider: p.ProviderCode,
    display: p.DefaultDisplayText || null,
    isRange,
    amounts,
    receive: isRange ? `${receive(netReceive(min))} – ${receive(netReceive(max))}` : receive(netReceive(min)),
    price: isRange
      ? `${fmtPrice(Number(min.SendValue))} – ${fmtPrice(Number(max.SendValue))}`
      : (min.SendValue != null ? fmtPrice(Number(min.SendValue)) : '—'),
  };
}

function RechargePage({ me, currency, onCurrency, landing, myCountries, carrierSlug }) {
  // The manifest lands after first paint, and the carrier groups below bake a
  // logo URL in at memo time — without this the tiles keep the empty result
  // they were built with and a carrier we do hold art for shows initials.
  const logosReady = useLogoManifest();
  const [phone, setPhone] = useState(() => {
    const pre = new URLSearchParams(window.location.search).get('phone');
    return pre && pre.replace(/\D/g, '').length >= 6 ? pre : '';
  });
  const [busy, setBusy] = useState(false);
  // True only while the country on screen is the geo guess nobody chose.
  const geoSeeded = useRef(false);
  const [country, setCountry] = useState(() => {
    // Country landing pages preset their country; region hubs and other
    // pages link here with ?country=XX.
    if (landing) {
      return { code: landing.iso, name: landing.name, dial: dialFor(landing.iso) };
    }
    const pre = new URLSearchParams(window.location.search).get('country');
    if (pre && /^[A-Za-z]{2}$/.test(pre)) {
      const code = pre.toUpperCase();
      return { code, name: countryName(code), dial: dialFor(code) };
    }
    const pref = readCountryPref();
    if (pref) return { code: pref, name: countryName(pref), dial: dialFor(pref) };
    // Nobody has chosen: start from where the visitor actually is. People top
    // up numbers in their own country far more often than anywhere else, so
    // geo is a strong default here — unlike the eSIM pages, where it says
    // nothing about the destination being shopped for. Thailand remains the
    // last resort, for when detection has nothing at all.
    const geo = (SERVER_LOCALE.country || '').toUpperCase();
    if (/^[A-Z]{2}$/.test(geo)) {
      geoSeeded.current = true;
      return { code: geo, name: countryName(geo), dial: dialFor(geo) };
    }
    return { code: 'TH', name: 'Thailand', dial: '66' };
  });
  const [countries, setCountries] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [ccOpen, setCcOpen] = useState(false);

  // Hero photo for whichever country is on screen. Landing pages included —
  // /topup/united-states used to render the Thailand temple because this was
  // hard-coded to DEFAULT_HERO.
  const [heroUrl, setHeroUrl] = useState(null);
  useEffect(() => {
    const pinned = PINNED.find((x) => x.code === country.code);
    if (pinned) return setHeroUrl(pinned.hero);
    let dead = false;
    resolveHero(country.name, (url) => { if (!dead) setHeroUrl(url); }, country.code);
    return () => { dead = true; };
  }, [country.code, country.name]);

  const [browse, setBrowse] = useState(null); // Thai (Wondd) carriers + amounts
  const [intlBrowse, setIntlBrowse] = useState(null); // Reloadly operators for non-TH
  const [browseError, setBrowseError] = useState(false);
  const [phoneHint, setPhoneHint] = useState(false);
  const [prodModal, setProdModal] = useState(null); // { group, e164 }
  const [pickModal, setPickModal] = useState(null); // { e164 } — carrier not matched
  const [buyModal, setBuyModal] = useState(null); // { group, product, value }
  const [phoneFocus, setPhoneFocus] = useState(false);
  const phoneRef = useRef(null);

  // Thailand airtime runs on Wondd (via partners API); every other country's
  // mobile tab is served by the Reloadly catalog. Games stay on Ding.
  const isThai = country.code === 'TH';

  // Whatever country this page shows IS the visitor's current country —
  // landings and ?country= presets included — so /gifts and the eSIM pages
  // continue from it.
  //
  // The geo guess is the exception: storing it would turn a detection into a
  // decision that outranks detection forever after, so someone who first
  // visited from one country would keep seeing it after moving. A real pick
  // writes the preference in pickBrowseCountry, so nothing is lost.
  useEffect(() => {
    if (geoSeeded.current) { geoSeeded.current = false; return; }
    writeCountryPref(country.code);
  }, [country.code]);

  // The initial dial code comes from the static table; once Ding's country
  // list loads, its prefix wins (it carries NANP area codes: AG is 1268, not 1).
  // Also strip the dial prefix from the phone input if it arrived as full E.164.
  useEffect(() => {
    if (!countries.length) return;
    const full = countries.find((x) => x.code === country.code);
    if (full && full.dial && full.dial !== country.dial) {
      setCountry((c) => ({ ...c, dial: full.dial }));
      // Strip leading +<dial> from the phone field so it isn't doubled
      setPhone((p) => {
        if (!p) return p;
        const digits = p.replace(/[^\d]/g, '');
        if (digits.startsWith(full.dial)) return digits.slice(full.dial.length);
        return p;
      });
    }
  }, [countries, country.code, country.dial]);

  function pickBrowseCountry(code, name) {
    // From a landing page, switching country moves to that country's own
    // landing URL — keeps the SEO structure and one page per country.
    if (landing && code !== country.code && SLUG_BY_ISO[code]) {
      window.location.href = `/topup/${SLUG_BY_ISO[code]}`;
      return;
    }
    const full = countries.find((x) => x.code === code);
    writeCountryPref(code);
    saveSearchedCountry(code);
    track('country_view', { country: code, product_type: 'airtime' });
    setCountry({ code, name, dial: (full && full.dial) || dialFor(code) });
    setPickerOpen(false);
    setCcOpen(false);
  }

  useEffect(() => {
    fetch('/api/topup/countries')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.status === 'ok' && Array.isArray(d.data)) {
          setCountries(d.data
            .map((c) => ({
              code: c.CountryIso,
              name: c.CountryName || countryName(c.CountryIso),
              flag: /^[A-Z]{2}$/.test(c.CountryIso || '') ? flagEmoji(c.CountryIso) : '🌐',
              dial: (c.InternationalDialingInformation && c.InternationalDialingInformation[0] && c.InternationalDialingInformation[0].Prefix) || null,
            }))
            .sort((a, b) => a.name.localeCompare(b.name)));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let dead = false;
    setBrowse(null);
    setIntlBrowse(null);
    setBrowseError(false);
    // Thailand airtime comes from Wondd; every other country from
    // the Reloadly catalog. Games and gifts live on /gifts now.
    if (country.code === 'TH') {
      fetch('/api/topup/wondd/browse')
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((d) => {
          if (dead) return;
          if (d && d.status === 'ok') setBrowse(d);
          else setBrowseError(true);
        })
        .catch(() => { if (!dead) setBrowseError(true); });
    } else {
      fetch(`/api/airtime/browse/${country.code}`)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((d) => {
          if (dead) return;
          if (d && d.status === 'ok') setIntlBrowse(d);
          else setBrowseError(true);
        })
        .catch(() => { if (!dead) setBrowseError(true); });
    }
    return () => { dead = true; };
  }, [country.code]);

  function focusPhone() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (phoneRef.current) phoneRef.current.focus();
  }

  const catalogLoaded = isThai ? browse != null : intlBrowse != null;

  // Number submitted: detect the carrier and open the options modal.
  // Uses the unified carrier lookup (Cache → Ding → Reloadly → Twilio)
  // for all countries. If no carrier is matched, the full catalog is
  // scrolled into view so the user can pick manually.
  async function findProducts() {
    const e164 = toE164(phone, country.dial);
    if (!e164) {
      setPhoneHint(true);
      focusPhone();
      return;
    }
    setPhoneHint(false);
    saveRecentPhone(e164);
    setBusy(true);
    try {
      let matchedGroup = null;
      try {
        const r = await fetch(`/api/carrier/lookup?phone=${encodeURIComponent(e164)}&country=${country.code}`);
        if (r.ok) {
          const d = await r.json();
          if (d && d.status === 'ok' && (d.carrier || d.providerCode)) {
            // Match by carrierCode (Thai: ais/dtac/truemove)
            if (d.carrierCode) {
              matchedGroup = grouped.find((g) => g.code === d.carrierCode);
            }
            // Match by Ding provider code — exact, and the only match that
            // works when Ding has no name for the provider (#298)
            if (!matchedGroup && d.providerCode) {
              matchedGroup = grouped.find((g) => g.providerCode === d.providerCode);
            }
            // Match by name (international operators)
            if (!matchedGroup && d.carrier) {
              const name = d.carrier.toLowerCase();
              matchedGroup = grouped.find((g) => {
                const gn = g.name.toLowerCase();
                return gn === name || gn.includes(name) || name.includes(gn)
                  || gn.split(/\s/)[0] === name.split(/\s/)[0];
              });
            }
          }
        }
      } catch (err) { /* fall through */ }

      if (matchedGroup && matchedGroup.products.length) {
        setProdModal({ group: matchedGroup, e164, allGroups: grouped });
        return;
      }
      // Single carrier for this country — use it directly
      if (grouped.length === 1 && grouped[0].products.length) {
        setProdModal({ group: grouped[0], e164, allGroups: grouped });
        return;
      }
      // No carrier matched — ask which one it is. Scrolling the grid into
      // view with no explanation left customers tapping "Request it" for a
      // carrier that was on the page (#298).
      if (grouped.length > 1) {
        setPickModal({ e164 });
        return;
      }
      window.scrollTo({ top: 420, behavior: 'smooth' });
    } finally {
      setBusy(false);
    }
  }

  // Recent numbers matching what's typed, for the autocomplete dropdown.
  // A local "0800…" matches its stored "+66800…" form: the leading zero is
  // dropped before comparing.
  const phoneSugs = useMemo(() => {
    if (!phoneFocus) return [];
    const all = readRecentPhones();
    if (!all.length) return [];
    const digits = phone.replace(/\D/g, '').replace(/^0+/, '');
    return all
      .filter((n) => !digits || n.replace(/\D/g, '').includes(digits))
      .filter((n) => {
        // Hide once the field already holds the full number
        const nd = n.replace(/\D/g, '');
        return !(digits.length >= 8 && nd.endsWith(digits));
      })
      .slice(0, 5);
  }, [phone, phoneFocus]);

  // A product, an amount and a number are everything checkout needs — no
  // login, no cart.
  function startTopup(product, group, value, e164Override) {
    const e164 = e164Override || toE164(phone, country.dial);
    if (!e164) {
      // Product-first path: collect the number in the detail modal
      setBuyModal({ product, group, value });
      return;
    }
    setPhoneHint(false);
    saveRecentPhone(e164);
    if (product.wonddCarrier) {
      // Wondd (Thailand) — carrier + THB amount
      const qs = new URLSearchParams({
        type: 'dd',
        phone: e164,
        iso: 'TH',
        carrier: product.wonddCarrier,
        amount: String(value),
      });
      if (product.isValidity) qs.set('validity', '1');
      window.location.href = `/checkout?${qs.toString()}`;
      return;
    }
    if (product.operatorId) {
      // A RANGE product needs a typed amount; never send "null" to checkout
      if (value == null) return;
      // Reloadly product — the intl checkout quote prices it in USD
      const qs = new URLSearchParams({
        type: 'intl',
        phone: e164,
        iso: country.code,
        operator: String(product.operatorId),
        value: String(value),
      });
      window.location.href = `/checkout?${qs.toString()}`;
      return;
    }
    const qs = new URLSearchParams({
      phone: e164,
      iso: country.code,
      provider: product.provider,
      providerName: group.name,
      sku: product.sku,
    });
    if (value != null) qs.set('value', String(value));
    window.location.href = `/checkout?${qs.toString()}`;
  }

  const grouped = useMemo(() => {
    // Outside Thailand: Reloadly operators, already popularity-sorted by the
    // server. Some markets (e.g. the UK) list every carrier under the
    // bundle/PIN categories, so all three feed the page.
    if (!isThai) {
      const ops = intlBrowse
        ? [
          ...(intlBrowse.operators || []),
          ...(intlBrowse.bundle_operators || []),
          ...(intlBrowse.pin_operators || []),
        ]
        : [];
      return ops.map(intlOperatorGroup).filter((g) => g.products.length);
    }
    // Thailand: Wondd carriers with THB amounts
    if (!browse || !browse.carriers) return [];
    return browse.carriers.map((carrier) => {
      const products = [{
        wonddCarrier: carrier.code,
        isRange: false,
        amounts: carrier.amounts.map((a) => ({
          value: a.thb,
          receive: currency === 'THB' ? null : `${a.thb} THB`,
          price: currency === 'THB' ? fmtBaht(a.thb) : fmtPrice(Number(a.usd)),
        })),
      }];
      // Validity extension products
      if (carrier.validity && carrier.validity.length) {
        products.push({
          wonddCarrier: carrier.code,
          isRange: false,
          isValidity: true,
          amounts: carrier.validity.map((v) => ({
            value: v.thb,
            receive: `${v.months} Months Validity`,
            price: currency === 'THB' ? fmtBaht(v.thb) : fmtPrice(Number(v.usd)),
          })),
        });
      }
      return {
        code: carrier.code,
        name: carrier.name,
        logo: carrierLogo(carrier.name, carrier.logo),
        products,
      };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browse, intlBrowse, country.name, isThai, fxStamp(), logosReady]);

  // A tile click opens that carrier's rows and scrolls to them.
  const [focusCarrier, setFocusCarrier] = useState(null);
  function pickCarrierTile(code) {
    setFocusCarrier((cur) => (cur === code ? null : code));
    window.requestAnimationFrame(() => {
      const el = document.querySelector('.carriergrid-wrap');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  // Category tabs — Thailand (Wondd) has no category concept, everything
  // else does (real fields for Reloadly/Zendit/Ding, see lib/airtimeCatalog.js).
  const [activeCategory, setActiveCategory] = useState('all');
  const categoryCounts = useMemo(() => {
    const c = { airtime: 0, pin: 0, bundle: 0 };
    for (const g of grouped) if (c[g.category] != null) c[g.category] += 1;
    return c;
  }, [grouped]);
  const categoryTabs = useMemo(() => ([
    { key: 'all', label: 'All' },
    { key: 'airtime', label: 'Airtime' },
    { key: 'pin', label: 'PIN' },
    { key: 'bundle', label: 'Bundles' },
  ].filter((t) => t.key === 'all' || categoryCounts[t.key] > 0)), [categoryCounts]);
  const visibleGrouped = useMemo(() => (
    isThai || activeCategory === 'all' ? grouped : grouped.filter((g) => g.category === activeCategory)
  ), [grouped, activeCategory, isThai]);

  // Suppliers list the same brand several times — NET10 appears five times in
  // the US catalogue, Pure four — so the wall would repeat one logo over and
  // over. Fold those into a single tile whose products are the union; each row
  // still carries its own product, so checkout is unaffected.
  const brands = useMemo(() => {
    const byBrand = new Map();
    for (const g of visibleGrouped) {
      const key = String(g.name || '').toLowerCase()
        .replace(/\s*\(.*?\)/g, '')
        .replace(/\s+(rtr|pins?|paygo|cash card|gsm plan|gsm|unlimited monthly|unlimited nationwide|unlimited|nationwide|family plan|monthly|30-day|data|minutes|lifeline|prepaid|wireless|bundles?)\b.*$/i, '')
        .replace(/\s+(usa|us|uk|england|canada|mexico)$/i, '')
        .trim() || g.code;
      const hit = byBrand.get(key);
      if (hit) hit.products = hit.products.concat(g.products);
      else byBrand.set(key, { ...g, products: g.products.slice() });
    }
    return Array.from(byBrand.values());
  }, [visibleGrouped]);

  // The feeds come popularity-sorted, so the first dozen are the majors.
  const majorBrands = useMemo(() => brands.slice(0, 12), [brands]);
  const minorBrands = useMemo(() => brands.slice(12), [brands]);
  const openGroup = useMemo(
    () => brands.find((g) => g.code === focusCarrier) || null,
    [brands, focusCarrier],
  );

  // /topup/<country>/<carrier> deep link — pre-open that carrier's rows once
  // the catalog loads. Best-effort by name (there's no shared code across
  // suppliers to match on — see docs/URL_STRUCTURE.md); a miss just leaves
  // the full grid showing, same as a plain /topup/<country> visit.
  const carrierAutoFocused = useRef(false);
  useEffect(() => {
    if (!carrierSlug || carrierAutoFocused.current || !brands.length) return;
    carrierAutoFocused.current = true;
    const hit = brands.find((b) => slugifyCarrierName(b.name) === carrierSlug);
    if (hit) pickCarrierTile(hit.code);
  }, [carrierSlug, brands]);

  // Landing pages already carry FAQ/SEO copy for their locked country; a
  // plain /recharge visit computes the same thing for whichever country is
  // currently selected, so every view gets a footer FAQ, not just landings.
  const pageCopy = landing || TOPUP_COPY[country.code] || genericTopupCopy(country.name);

  return (
    <div className="page">
      <Header me={me} active="recharge" currency={currency} onCurrency={onCurrency} />

      <section className="hero" style={heroStyle(heroUrl)}>
        <Breadcrumb active="recharge" iso={country.code} countryName={country.name}
          onCountryClick={() => setPickerOpen((o) => !o)} />
        <h1 className="hero-title">
          {/* The country itself is the control — no separate "Change country"
              label. Landings use the same shape as /recharge so every country
              reads grammatically; the bespoke landing.h1 copy is retired. */}
          Top up a prepaid phone in{' '}
          <span className="picker-anchor">
            <button type="button" className="changec"
              title="Change country" aria-label={`Change country — currently ${country.name}`}
              onClick={() => setPickerOpen((o) => !o)}>{country.name}</button>
            {pickerOpen && (
              <CountryPicker
                countries={countries}
                current={country.code}
                onPick={pickBrowseCountry}
                onClose={() => setPickerOpen(false)}
                popular={['TH', 'PH', 'ID', 'IN', 'MM']}
              />
            )}
          </span>
        </h1>
        <form
          className="hero-topup"
          onSubmit={(e) => {
            e.preventDefault();
            if (!busy) findProducts();
          }}
        >
          <div className="phone-input big">
            <span className="picker-anchor">
              <button type="button" className="cc ccbtn" onClick={() => setCcOpen((o) => !o)} aria-label="Choose country code">
                {country.code && /^[A-Z]{2}$/.test(country.code) ? flagEmoji(country.code) : '🌐'} +{country.dial || 'XX'} ▾
              </button>
              {ccOpen && (
                <CountryPicker
                  countries={countries.filter((c) => c.dial)}
                  current={country.code}
                  onPick={pickBrowseCountry}
                  onClose={() => setCcOpen(false)}
                  popular={['TH', 'PH', 'ID', 'IN', 'MM']}
                />
              )}
            </span>
            <input
              ref={phoneRef}
              className="num" type="tel" autoFocus={!isMobileViewport()} value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onFocus={() => setPhoneFocus(true)}
              onBlur={() => setPhoneFocus(false)}
              placeholder={isMobileViewport() ? 'Phone number' : 'Enter a phone number — we detect the carrier'}
              aria-label="Phone number to top up"
            />
          </div>
          {phoneSugs.length > 0 && (
            <div className="suggest phone-suggest">
              {phoneSugs.map((n) => (
                <button type="button" key={n} onMouseDown={(e) => { e.preventDefault(); setPhone(n); setPhoneFocus(false); }}>
                  <span className="sname">{n}</span>
                </button>
              ))}
            </div>
          )}
          <button type="submit" className="btn hero-btn" disabled={busy}>
            {busy ? 'Finding carrier…' : 'Topup'}
          </button>
        </form>
        {landing && <TrustStrip />}
      </section>


      <div className={`body${landing ? ' nolrail' : ''}`}>
        <main className="main">
          {!isThai && catalogLoaded && categoryTabs.length > 1 && (
            <div className="seg dark" role="tablist" style={{ marginBottom: 14 }}>
              {categoryTabs.map((t) => (
                <button type="button" key={t.key} className={activeCategory === t.key ? 'on' : ''}
                  onClick={() => setActiveCategory(t.key)}>{t.label}</button>
              ))}
            </div>
          )}
          {/* Mobile gets the one-line cards-summary instead */}
          {!landing && (
            <div className="sortrow hide-mobile">
              <span className="count">
                {catalogLoaded ? `${visibleGrouped.length} carriers for ` : 'Loading carriers for '}
                <b>{country.name}</b>
              </span>
              <span className={`paynote${phoneHint ? ' warn' : ''}`}>
                {phoneHint
                  ? 'Enter a valid phone number above, then pick an amount'
                  : 'Pick an amount to check out — no account needed'}
              </span>
            </div>
          )}

          {catalogLoaded && !browseError && brands.length === 0 && (
            <NoTopupProducts country={country} />
          )}

          {!browseError && (catalogLoaded ? brands.length > 0 : true) && (
            <>
              <CarrierTiles majors={majorBrands} minors={minorBrands} iso={country.code}
                openCode={focusCarrier} onPick={pickCarrierTile} />
              <CarrierAmounts group={openGroup} startTopup={startTopup}
                onBack={() => setFocusCarrier(null)} />
              {catalogLoaded && <RequestProductLink label="Can't find your carrier? Request it" defaultType="carrier" country={country.code} className="request-cta" />}
            </>
          )}

          {browseError && <div className="notice">Couldn’t load carriers for {country.name}. Try another country or enter a number above.</div>}

          {landing && <StepsStrip steps={TOPUP_STEPS} />}
        </main>

        {!landing && (
        <aside className="rail">
          <div className="railcard trust">
            <h4>How it works</h4>
            Enter the phone number, we detect the carrier and show the exact amounts —
            credit is delivered to the phone instantly after payment.
          </div>
          <SupportCard />
        </aside>
        )}
      </div>

      <div className="page-footer">
        <PayBox />
        <TrustBadge />
        <FaqSection faq={pageCopy.faq} seo={pageCopy.seo} />
      </div>

      <SiteFooterV2 currency={currency} onCurrency={onCurrency} />

      {prodModal && (
        <ProductOptionsModal
          group={prodModal.group} e164={prodModal.e164}
          allGroups={prodModal.allGroups}
          onClose={() => setProdModal(null)}
          onPick={(prod, value) => startTopup(prod, prodModal.group, value, prodModal.e164)}
          onChangeGroup={(g) => setProdModal({ ...prodModal, group: g })}
        />
      )}
      {pickModal && (
        <CarrierPickModal
          e164={pickModal.e164} groups={grouped}
          onClose={() => setPickModal(null)}
          onPick={(g) => {
            setPickModal(null);
            setProdModal({ group: g, e164: pickModal.e164, allGroups: grouped });
          }}
        />
      )}
      {buyModal && (
        <TopupDetailModal
          group={buyModal.group} product={buyModal.product} value={buyModal.value}
          country={country} initialPhone={phone}
          onClose={() => setBuyModal(null)}
          onConfirm={(e164) => startTopup(buyModal.product, buyModal.group, buyModal.value, e164)}
        />
      )}
    </div>
  );
}

function CarrierTiles({ majors, minors, openCode, onPick, iso }) {
  useLogoManifest(); // redraw once the brand colours land
  if (!majors.length) return null;
  const tile = (g, small) => {
    // The name block wears the carrier's own colours, read off its logo.
    const c = carrierColors(g.name, iso);
    const main = (c && c.main) || 'var(--teal)';
    const accent = (c && c.accent) || '#fff';
    const isOpen = openCode === g.code;
    return (
      <button type="button" key={g.code}
        className={`ctile${small ? ' sm' : ''}${isOpen ? ' chosen' : ''}${openCode && !isOpen ? ' fading' : ''}`}
        onClick={() => onPick(g.code)}>
        <span className="ctile-logo">
          {g.logo
            ? <img src={g.logo} alt="" loading="lazy" />
            : <span className="ctile-ph">{(g.name || '?').slice(0, 2)}</span>}
        </span>
        <span className="ctile-meta" style={{ background: main, color: accent }}>{g.name}</span>
      </button>
    );
  };
  return (
    <section className="carriergrid-wrap">
      <h2 className="cg-h2">Choose the carrier</h2>
      <div className={`carriergrid${openCode ? ' choosing' : ''}`}>{majors.map((g) => tile(g, false))}</div>
      {minors.length > 0 && (
        <>
          <p className={`cg-more${openCode ? ' hidden' : ''}`}>More carriers</p>
          <div className={`carriergrid small${openCode ? ' choosing' : ''}`}>{minors.map((g) => tile(g, true))}</div>
        </>
      )}
    </section>
  );
}

// The amounts for the carrier just tapped. They open in place, under the tile,
// with the rest of the wall faded away — on a phone that is the whole screen,
// so nothing needs a second scroll to reach.
function CarrierAmounts({ group: g, onBack, startTopup }) {
  const [customFor, setCustomFor] = useState(null); // operatorId currently entering a custom amount
  const [customValue, setCustomValue] = useState('');
  useEffect(() => { setCustomFor(null); setCustomValue(''); }, [g && g.code]);

  if (!g) return null;
  const rangeProducts = g.products.filter((prod) => prod.isRange);
  const rows = g.products.filter((prod) => !prod.isRange)
    .flatMap((prod) => (prod.amounts || [null]).map((a) => ({ prod, a })));

  function submitCustom(prod) {
    const v = parseFloat(customValue);
    if (!Number.isFinite(v) || v < (prod.min || 0) || v > (prod.max || Infinity)) return;
    startTopup(prod, g, Math.round(v * 100) / 100);
  }

  return (
    <div className="opened">
      <button type="button" className="backall" onClick={onBack}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        Back
      </button>
      {rangeProducts.map((prod) => (
        <div className="amt-custom" key={prod.operatorId}>
          {customFor === prod.operatorId ? (
            <form className="amt-custom-form" onSubmit={(e) => { e.preventDefault(); submitCustom(prod); }}>
              <label>
                Enter an amount ({fmtPrice(prod.min || 0)} – {fmtPrice(prod.max || 0)})
                <input type="number" inputMode="decimal" autoFocus
                  min={prod.min || 0} max={prod.max || undefined} step="0.01"
                  value={customValue} onChange={(e) => setCustomValue(e.target.value)}
                  placeholder={`e.g. ${prod.max || 50}`} />
              </label>
              <div className="amt-custom-actions">
                <button type="button" className="linkish" onClick={() => { setCustomFor(null); setCustomValue(''); }}>Cancel</button>
                <button type="submit" className="btn small">Continue</button>
              </div>
            </form>
          ) : (
            <button type="button" className="amt amt-custom-tile"
              onClick={() => { setCustomFor(prod.operatorId); setCustomValue(''); }}>
              <span className="amt-val">Choose your own amount</span>
              <span className="amt-recv">{fmtPrice(prod.min || 0)} – {fmtPrice(prod.max || 0)}</span>
            </button>
          )}
        </div>
      ))}
      <div className="amts">
        {rows.map(({ prod, a }, i) => (
          <button type="button" className="amt"
            key={`${prod.sku || prod.operatorId || prod.wonddCarrier}-${a ? a.value : 'x'}`}
            style={{ animationDelay: `${Math.min(i * 45, 450)}ms` }}
            onClick={() => startTopup(prod, g, a && a.value)}>
            <span className="amt-val">{a && a.receive ? a.receive : (a ? a.price : prod.price)}</span>
            {a && a.receive && String(a.receive) !== String(a.price)
              && <span className="amt-recv">Price {a.price}</span>}
          </button>
        ))}
      </div>
      <p className="amt-allin">
        <b>No hidden fees.</b> Card and USDC add a small processing fee, your MobileTopup
        balance adds none — the exact total is shown before you confirm.
      </p>
    </div>
  );
}

// Nothing to top up here — but we almost certainly sell a data plan for the
// country, so offer that rather than leaving a dead end.
function NoTopupProducts({ country }) {
  const slug = SLUG_BY_ISO[country.code];
  return (
    <div className="notice empty-cta">
      <p>No mobile topup products for {country.name} right now.</p>
      {slug && (
        <a className="btn esim-cta" href={`/esim/${slug}`}>
          Get a {country.name} data plan instead
        </a>
      )}
      <RequestProductLink label="Request this carrier or country" defaultType="carrier" country={country.code} />
    </div>
  );
}

function GiftGroup({ group: g, collapsed: startCollapsed, onBuy }) {
  const [open, setOpen] = useState(!startCollapsed);
  return (
    <React.Fragment>
      <tr className={`grp${startCollapsed ? ' clickable' : ''}`} onClick={startCollapsed ? () => setOpen((o) => !o) : undefined}>
        <td colSpan="4">
          {g.logo
            ? <img className="carrier-logo sm" src={g.logo} alt="" loading="lazy" />
            : <span className="carrier-logo sm ph">{(g.name || '?').slice(0, 2)}</span>}
          {g.name}
          {startCollapsed && <span className="grp-toggle">{open ? '▾' : '▸'} {g.products.length}</span>}
        </td>
      </tr>
      {open && g.products.map((p) => (
        <tr key={p.sku} className="planrow" onClick={() => onBuy(p, g)}>
          <td className="name clamp">{p.display || g.name}</td>
          <td className="wrap">{p.receive}</td>
          <td className="num price">{p.price}</td>
          <td><button type="button" className="btn small" onClick={(e) => { e.stopPropagation(); onBuy(p, g); }}>Buy</button></td>
        </tr>
      ))}
    </React.Fragment>
  );
}

// Sticky category rail. Highlights the section currently in view.
function GiftRail({ sections }) {
  const [on, setOn] = useState(sections[0] && sections[0].id);
  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined;
    const io = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (vis) setOn(vis.target.id.replace(/^gift-/, ''));
    }, { rootMargin: '-60px 0px -70% 0px' });
    sections.forEach((s) => { const el = document.getElementById(`gift-${s.id}`); if (el) io.observe(el); });
    return () => io.disconnect();
  }, [sections]);

  function go(e, id) {
    e.preventDefault();
    const el = document.getElementById(`gift-${id}`);
    if (!el) return;
    window.scrollBy({ top: el.getBoundingClientRect().top - 60, behavior: 'smooth' });
    setOn(id);
  }

  return (
    <nav className="gift-rail" aria-label="Categories">
      {sections.map((s) => (
        <a key={s.id} href={`#gift-${s.id}`} className={on === s.id ? 'on' : ''} onClick={(e) => go(e, s.id)}>{s.label}</a>
      ))}
    </nav>
  );
}

// Range of what the recipient actually gets, in the voucher's own currency —
// not what the customer is charged (that already includes our markup, and
// for non-USD cards would be a different currency entirely).
function giftRange(g) {
  const vals = g.products.map((p) => p.recvMin).filter((v) => v != null);
  if (!vals.length) return '';
  const iso = (g.products.find((p) => p.recvCurrency) || {}).recvCurrency || 'USD';
  const fmt = (v) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency', currency: iso, currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: Number.isInteger(v) ? 0 : 2, maximumFractionDigits: 2,
      }).format(v);
    } catch (e) { return `${v} ${iso}`; }
  };
  const lo = Math.min(...vals); const hi = Math.max(...vals);
  return lo === hi ? fmt(lo) : `${fmt(lo)} – ${fmt(hi)}`;
}

function GiftCard({ g, onPick }) {
  return (
    <button type="button" className="gcard" onClick={() => onPick(g.code)}>
      {g.logo
        ? <img className="gcard-logo" src={g.logo} alt="" loading="lazy" />
        : <span className="gcard-logo ph">{(g.name || '?').slice(0, 1)}</span>}
      <span className="gcard-txt">
        <span className="gcard-name">{g.name}</span>
        <span className="gcard-range">{giftRange(g)}</span>
      </span>
      <span className="gcard-n">▸{g.products.length}</span>
    </button>
  );
}

const SECTION_CAP = 10;

function GiftSection({ section: s, iso, onPick }) {
  const [all, setAll] = useState(false);
  const items = all ? s.items : s.items.slice(0, SECTION_CAP);
  return (
    <section id={`gift-${s.id}`} className="gift-section">
      <header>
        <h2 className="cg-h2">{s.label}</h2>
        <span className="gift-count">{s.items.length} brands</span>
        {s.items.length > SECTION_CAP && !all && (
          <button type="button" className="gift-seeall" onClick={() => setAll(true)}>See all ›</button>
        )}
      </header>
      <div className="gift-grid">
        {items.map((g) => <GiftCard key={g.code} g={g} onPick={onPick} />)}
      </div>
    </section>
  );
}

// Step 2: the brand just tapped, in the same "chosen tile" the recharge page
// uses, with amounts as a grid and one Pay button.
function GiftPicked({ group: g, onBack, onBuy }) {
  const [i, setI] = useState(Math.min(2, g.products.length - 1));
  useEffect(() => { setI(Math.min(2, g.products.length - 1)); }, [g.code, g.products.length]);
  const p = g.products[i] || g.products[0];
  return (
    <div className="gift-picked">
      <div className="carriergrid choosing">
        <button type="button" className="ctile chosen" onClick={onBack} aria-label="Back to all gift cards">
          <span className="ctile-logo">
            {g.logo ? <img src={g.logo} alt="" /> : <span className="ctile-ph">{(g.name || '?').slice(0, 2)}</span>}
          </span>
          <span className="ctile-meta" style={{ background: 'var(--teal)', color: '#fff' }}>
            {g.name}
            <span className="gift-picked-sub">{g.products.length} options · code by email in minutes</span>
          </span>
        </button>
      </div>

      <button type="button" className="backall" onClick={onBack}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        All gift cards
      </button>

      <div className="amt-grid">
        {g.products.map((prod, k) => (
          <button type="button" key={prod.sku} className={`amt-tile${k === i ? ' on' : ''}`}
            style={{ animationDelay: `${Math.min(k * 40, 400)}ms` }} onClick={() => setI(k)}>
            <span className="amt-val">{prod.receive}</span>
            <span className="amt-recv">Price {prod.price}</span>
          </button>
        ))}
      </div>

      {p && (
        <div className="gift-buybar">
          <span className="gift-buybar-txt">
            <span className="gift-buybar-sub">{g.name} eGift</span>
            <b>{p.receive}</b>
          </span>
          <button type="button" className="btn" onClick={() => onBuy(p, g)}>Pay {p.price}</button>
        </div>
      )}
    </div>
  );
}

///////////////////////////////////////////////////////////////////////////////
// Gift buy modal — collect the recipient name before going to checkout.
// Unlike mobile topups, gift products don't need a phone number; Zendit
// vouchers require recipient.firstName/lastName for fulfillment.
///////////////////////////////////////////////////////////////////////////////
function GiftBuyModal({ product, group, country, onClose, onConfirm }) {
  const [acct, setAcct] = useState('');
  const [hint, setHint] = useState(false);
  const [brandInfo, setBrandInfo] = useState(null);
  const [instructions, setInstructions] = useState(null);
  const inputRef = useRef(null);

  // Fetch brand info + redemption instructions when modal opens
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => { if (inputRef.current) inputRef.current.focus(); }, 60);
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);

    // Fetch brand details and instructions from Zendit
    const brandKey = group.brandKey;
    if (brandKey) {
      fetch(`/api/gifts/brand/${encodeURIComponent(brandKey)}`)
        .then((r) => r.ok ? r.json() : null)
        .then((d) => { if (d && d.brand) setBrandInfo(d.brand); })
        .catch(() => {});
      fetch(`/api/gifts/instructions/${encodeURIComponent(brandKey)}?country=${country.code}`)
        .then((r) => r.ok ? r.json() : null)
        .then((d) => { if (d) setInstructions(d); })
        .catch(() => {});
    }

    return () => {
      document.body.style.overflow = '';
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, group.brandKey, country.code]);

  function confirm(e) {
    e.preventDefault();
    const val = acct.trim();
    if (!val) { setHint(true); if (inputRef.current) inputRef.current.focus(); return; }
    onConfirm(val);
  }

  const logo = (brandInfo && brandInfo.logo) || group.logo;

  return (
    <div className="modal-backdrop sheet-top" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal prod-modal" role="dialog" aria-modal="true" aria-label={`${group.name} purchase`}>
        <div className="modal-head">
          <div>
            <div className="modal-title">
              {logo && <img className="carrier-logo" src={logo} alt="" />}
              {group.name}
            </div>
            <div className="modal-chips">
              {product.receive && <span className="mchip">You get {product.receive}</span>}
              <span className="mchip">{countryName(country.code)}</span>
            </div>
          </div>
          <div className="modal-price"><b>{product.price}</b></div>
          <button type="button" className="modal-x" onClick={onClose} aria-label="Close">&#10005;</button>
        </div>

        <div className="modal-body">
          <form onSubmit={confirm}>
            <h5>Recipient name</h5>
            <input
              ref={inputRef}
              className="input big"
              type="text"
              value={acct}
              onChange={(e) => { setAcct(e.target.value); setHint(false); }}
              placeholder="Enter recipient's full name"
              autoComplete="name"
            />
            {hint && <p className="hint warn">Please enter the recipient's name.</p>}
            <button type="submit" className="btn block" style={{ marginTop: 12 }}>Continue to checkout</button>
          </form>

          {brandInfo && brandInfo.description && (
            <p className="modal-note" style={{ marginTop: 12, fontSize: 12, color: '#555', lineHeight: 1.45 }}>
              {brandInfo.description}
            </p>
          )}

          {instructions && instructions.redemptionInstructions && (
            <div className="modal-note" style={{ marginTop: 10 }}>
              <h5 style={{ fontSize: 12, marginBottom: 4 }}>How to redeem</h5>
              <div style={{ fontSize: 12, color: '#555', lineHeight: 1.45 }}
                dangerouslySetInnerHTML={{ __html: instructions.redemptionInstructions }} />
            </div>
          )}

          {instructions && instructions.terms && (
            <div className="modal-note" style={{ marginTop: 10 }}>
              <h5 style={{ fontSize: 12, marginBottom: 4 }}>Terms &amp; conditions</h5>
              <div style={{ fontSize: 11, color: '#777', lineHeight: 1.4, maxHeight: 120, overflowY: 'auto' }}
                dangerouslySetInnerHTML={{ __html: instructions.terms }} />
            </div>
          )}

          {!(instructions && instructions.terms) && (
            <ul className="modal-note terms-list" style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
              <li>Voucher code and redeem instructions delivered by email</li>
              <li>Non-refundable and valid for a single transaction</li>
              <li>Valid only in the {countryName(country.code)} region</li>
            </ul>
          )}

          <ul className="modal-note terms-list" style={{ marginTop: 8, fontSize: 11, color: '#999' }}>
            <li>By purchasing you agree to the <a href="/terms" target="_blank" rel="noopener">Terms of Service</a> and <a href="/refund-policy" target="_blank" rel="noopener">Refund Policy</a></li>
            <li>Digital vouchers cannot be returned, exchanged or refunded once delivered</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

///////////////////////////////////////////////////////////////////////////////
// /gifts — gift cards, game credit & utility payments: everything that is
// not airtime or eSIM. Only countries with at least one offer are listed,
// fed by /api/gifts/countries (weekly-synced Ding catalog).
///////////////////////////////////////////////////////////////////////////////
function GiftsPage({ me, currency, onCurrency, myCountries, landing }) {
  const [country, setCountry] = useState(() => {
    if (landing) return { code: landing.iso, name: landing.name };
    const pre = new URLSearchParams(window.location.search).get('country');
    if (pre && /^[A-Za-z]{2}$/.test(pre)) {
      const code = pre.toUpperCase();
      return { code, name: countryName(code) };
    }
    const pref = readCountryPref();
    if (pref) return { code: pref, name: countryName(pref) };
    return { code: 'TH', name: 'Thailand' };
  });
  const [countries, setCountries] = useState([]); // only countries with offers
  const [pickerOpen, setPickerOpen] = useState(false);
  const [browse, setBrowse] = useState(null);

  // Hero photo for whichever country is on screen. Landing pages included —
  // /topup/united-states used to render the Thailand temple because this was
  // hard-coded to DEFAULT_HERO.
  const [heroUrl, setHeroUrl] = useState(null);
  useEffect(() => {
    const pinned = PINNED.find((x) => x.code === country.code);
    if (pinned) return setHeroUrl(pinned.hero);
    let dead = false;
    resolveHero(country.name, (url) => { if (!dead) setHeroUrl(url); }, country.code);
    return () => { dead = true; };
  }, [country.code, country.name]);

  const [error, setError] = useState(false);

  useEffect(() => {
    fetch('/api/gifts/countries')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        if (d && d.status === 'ok' && Array.isArray(d.countries)) {
          setCountries(d.countries
            .filter((c) => /^[A-Z]{2}$/.test(c.iso || ''))
            .map((c) => ({ code: c.iso, count: c.count, name: countryName(c.iso), flag: flagEmoji(c.iso) }))
            .sort((a, b) => a.name.localeCompare(b.name)));
        }
      })
      .catch(() => { /* picker degrades to the preset country */ });
  }, []);

  // If the preset country has no gift offers, keep it selected but flag it
  // so the UI can show "no offers" instead of silently jumping to Bangladesh.
  const [noOffers, setNoOffers] = useState(false);
  useEffect(() => {
    if (!countries.length) return;
    setNoOffers(!countries.some((c) => c.code === country.code));
  }, [countries, country.code]);

  useEffect(() => {
    if (noOffers) { setBrowse(null); return; }
    let dead = false;
    setBrowse(null);
    setError(false);
    fetch(`/api/gifts/browse/${country.code}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => { if (!dead) { if (d && d.status === 'ok') setBrowse(d); else setError(true); } })
      .catch(() => { if (!dead) setError(true); });
    return () => { dead = true; };
  }, [country.code, noOffers]);

  const grouped = useMemo(() => {
    if (!browse) return [];
    return (browse.providers || []).map((prov) => ({
      code: prov.ProviderCode,
      name: (prov.Name || prov.ProviderCode).replace(new RegExp(` ${country.name}$`), ''),
      logo: carrierLogo(prov.Name, prov.LogoUrl),
      brandKey: prov.brandKey || null,
      products: (browse.products || [])
        .filter((p) => p.ProviderCode === prov.ProviderCode)
        .map((p) => ({
          ...dingProductRow(p),
          recvMin: p.Minimum && p.Minimum.ReceiveValue,
          recvCurrency: p.Minimum && p.Minimum.ReceiveCurrencyIso,
        })),
    })).filter((g) => g.products.length)
      .sort((a, b) => a.name.localeCompare(b.name));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browse, country.name, fxStamp()]);

  const totalProducts = useMemo(() => grouped.reduce((n, g) => n + g.products.length, 0), [grouped]);

  const [picked, setPicked] = useState(null);
  const [fading, setFading] = useState(false);
  useEffect(() => { setPicked(null); }, [country.code]);
  // /gifts/<country>?brand=<key> (from plain-language search) opens that
  // brand as soon as the catalog is in. Applied once, then ignored.
  const brandParam = useRef(new URLSearchParams(window.location.search).get('brand'));
  useEffect(() => {
    const want = brandParam.current;
    if (!want || !grouped.length) return;
    brandParam.current = null;
    const w = String(want).toLowerCase();
    const g = grouped.find((x) => (x.brandKey && String(x.brandKey).toLowerCase() === w) || String(x.code).toLowerCase() === `zendit-${w}`)
      || grouped.find((x) => String(x.name).toLowerCase().includes(w));
    if (g) setPicked(g.code);
  }, [grouped]);
  const byCode = useMemo(() => Object.fromEntries(grouped.map((g) => [g.code, g])), [grouped]);
  const sections = useMemo(() => {
    const gs = (browse && browse.groups) || [{ id: 'all', label: 'All gift cards', providerCodes: grouped.map((g) => g.code) }];
    return gs.map((s) => ({ ...s, items: s.providerCodes.map((c) => byCode[c]).filter(Boolean) }))
      .filter((s) => s.items.length);
  }, [browse, byCode, grouped]);
  function pick(code) {
    track('gift_brand_pick', { country: country.code, provider: code });
    setFading(true);
    setTimeout(() => { setPicked(code); setFading(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }, 260);
  }

  // Personal countries first (only those with offers), biggest catalogs fill up
  const pinned = useMemo(() => {
    const byCount = [...countries].sort((a, b) => b.count - a.count);
    const base = (myCountries || [])
      .map((code) => countries.find((c) => c.code === code))
      .filter(Boolean);
    for (const c of byCount) {
      if (base.length >= 8) break;
      if (!base.some((b) => b.code === c.code)) base.push(c);
    }
    return base;
  }, [countries, myCountries]);

  const [buyModal, setBuyModal] = useState(null);

  function pickCountry(code, name) {
    // From a /gifts/<country> landing page, switching country moves to that
    // country's own landing URL — keeps one indexed page per country, same
    // as /topup and /esim.
    if (landing && code !== country.code && SLUG_BY_ISO[code]) {
      window.location.href = `/gifts/${SLUG_BY_ISO[code]}`;
      return;
    }
    setPickerOpen(false);
    writeCountryPref(code);
    saveSearchedCountry(code);
    track('country_view', { country: code, product_type: 'gift' });
    setCountry({ code, name: name || countryName(code) });
  }

  function startGiftBuy(product, group, value) {
    setBuyModal({ product, group, value });
  }

  function confirmGiftBuy(accountId) {
    const p = buyModal.product;
    const g = buyModal.group;
    const qs = new URLSearchParams({
      type: 'gift',
      phone: accountId,
      iso: country.code,
      provider: p.provider,
      providerName: g.name,
      sku: p.sku,
    });
    if (buyModal.value != null) qs.set('value', String(buyModal.value));
    setBuyModal(null);
    window.location.href = `/checkout?${qs.toString()}`;
  }

  return (
    <div className="page">
      <Header me={me} active="games" currency={currency} onCurrency={onCurrency} />

      <section className="hero" style={heroStyle(heroUrl)}>
        <Breadcrumb active="gifts" iso={country.code} countryName={country.name}
          onCountryClick={() => setPickerOpen((o) => !o)} />
        <h1 className="hero-title">
          Gifts, games &amp; utilities in{' '}
          <span className="picker-anchor">
            <button type="button" className="gift-changec" aria-label={`Change country — currently ${country.name}`} onClick={() => setPickerOpen((o) => !o)}>
              <b>{country.name}</b>
              <span className="gift-changec-arrow" aria-hidden="true">▾</span>
            </button>
            {pickerOpen && (
              <CountryPicker
                countries={countries}
                current={country.code}
                onPick={pickCountry}
                onClose={() => setPickerOpen(false)}
                popular={pinned.slice(0, 5).map((c) => c.code)}
              />
            )}
          </span>
        </h1>
      </section>

      <div className="body gifts-body">
        <main className="main">
          <div className="sortrow hide-mobile">
            <span className="count">
              {browse ? `${grouped.length} providers for ` : 'Loading offers for '}
              <b>{country.name}</b>
            </span>
            <span className="paynote">Gift cards, game credit &amp; utilities — voucher codes delivered by email</span>
          </div>

          {error && <div className="notice">Couldn’t load offers for {country.name}. Try another country.</div>}

          {noOffers && <div className="notice">No gift or game offers available for {country.name}. Pick a different country above, or <RequestProductLink label="request a brand" defaultType="gift_card" country={country.code} />.</div>}

          {!error && !noOffers && browse == null && <div className="plan-cards"><CardsSkeleton /></div>}
          {!error && !noOffers && browse != null && (picked && byCode[picked]
            ? <GiftPicked group={byCode[picked]} onBack={() => setPicked(null)} onBuy={startGiftBuy} />
            : <>
                <GiftRail sections={sections} />
                <div className={`gift-catalog${fading ? ' fading' : ''}`}>
                  {sections.map((s) => <GiftSection key={s.id} section={s} iso={country.code} onPick={pick} />)}
                  {sections.length === 0 && <div className="notice">No offers for {country.name} right now — pick another country above, or <RequestProductLink label="request a brand" defaultType="gift_card" country={country.code} />.</div>}
                </div>
              </>
          )}
        </main>
      </div>

      <div className="page-footer">
        <TrustBadge />
        <div className="railcard"><h4>How it works</h4>
          <ul className="terms-list"><li>Voucher code delivered by email</li><li>Arrives within minutes of payment</li><li>No phone number needed</li></ul></div>
        <div className="railcard"><h4>How to redeem</h4>
          <ol className="terms-list" style={{ paddingLeft: 18 }}><li>Choose a product and amount</li><li>Enter the recipient's name</li><li>Pay — code arrives by email</li><li>Follow the redeem steps in the email</li></ol></div>
        <div className="railcard terms"><h4>Terms</h4>
          <ul className="terms-list"><li>Non-refundable, no cash exchange</li><li>Not replaceable if lost or stolen</li><li>Valid until the claim-by date</li><li>Region-specific</li><li>Brand terms apply</li><li><a href="/refund-policy" style={{ color: '#666' }}>Full refund policy</a></li></ul></div>
        <SupportCard />
        <PayBox />
        <FaqSection faq={GIFT_FAQ} />
      </div>

      <SiteFooterV2 currency={currency} onCurrency={onCurrency} />

      {buyModal && (
        <GiftBuyModal
          product={buyModal.product}
          group={buyModal.group}
          country={country}
          onClose={() => setBuyModal(null)}
          onConfirm={confirmGiftBuy}
        />
      )}
    </div>
  );
}

function CountryPicker({ countries, current, onPick, onClose, popular = [] }) {
  const [q, setQ] = useState('');
  const inputRef = useRef(null);
  const rootRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    function onClick(e) { if (rootRef.current && !rootRef.current.contains(e.target)) onClose(); }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [onClose]);

  const f = q.trim().toLowerCase();
  const list = f
    ? countries.filter((c) => c.name.toLowerCase().includes(f) || c.code.toLowerCase() === f)
    : countries;
  const popList = f ? [] : popular
    .map((code) => countries.find((c) => c.code === code))
    .filter(Boolean);

  return (
    <div className="picker" ref={rootRef}>
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={countries.length ? `Filter ${countries.length} countries…` : 'Loading countries…'}
        onKeyDown={(e) => { if (e.key === 'Enter' && list.length) { e.preventDefault(); onPick(list[0].code, list[0].name); } }}
      />
      <div className="picker-list">
        {popList.length > 0 && <div className="picker-sep">Popular</div>}
        {popList.map((c) => (
          <button
            type="button"
            key={`pop-${c.code}`}
            className={c.code === current ? 'current' : ''}
            onClick={() => onPick(c.code, c.name)}
          >
            <span className="flag">{c.flag}</span>
            <span className="sname">{c.name}</span>
            {c.plan_count != null && c.from_price != null && (
              <span className="smeta">{c.plan_count} · from {fmtPrice(c.from_price)}</span>
            )}
            {c.dial != null && c.plan_count == null && <span className="smeta">+{c.dial}</span>}
          </button>
        ))}
        {popList.length > 0 && <div className="picker-sep">All countries</div>}
        {list.map((c) => (
          <button
            type="button"
            key={c.code}
            className={c.code === current ? 'current' : ''}
            onClick={() => onPick(c.code, c.name)}
          >
            <span className="flag">{c.flag}</span>
            <span className="sname">{c.name}</span>
            {c.plan_count != null && c.from_price != null && (
              <span className="smeta">{c.plan_count} · from {fmtPrice(c.from_price)}</span>
            )}
            {c.dial != null && c.plan_count == null && <span className="smeta">+{c.dial}</span>}
          </button>
        ))}
        {!list.length && <div className="picker-empty">{countries.length ? 'No match.' : 'Loading…'}</div>}
      </div>
    </div>
  );
}


function PlanRow({ p, best, onOpen, onBuy }) {
  return (
    <tr className="planrow" onClick={() => onOpen(p)}>
      <td className="name">
        <span className="plan-link">{p.name}</span>
        {best && <span className="tag good">Best $/GB</span>}
      </td>
      <td>{p.data_label ? `${p.data_label}${p.is_daily ? '/day' : ''}` : '—'}</td>
      <td>{p.is_daily ? 'choose the days' : durationLabel(p)}</td>
      <td className="nets">{networksLabel(p)}</td>
      <td className="exit"><ExitCell v={p.data_exit} /></td>
      <td className="num price">{fmtPrice(p.price)}</td>
      <td className="num pergb">{p.per_gb != null ? p.per_gb.toFixed(2) : '—'}</td>
      <td>
        <button type="button" className="btn small" onClick={(e) => { e.stopPropagation(); onBuy(p); }}>Buy</button>
      </td>
    </tr>
  );
}

///////////////////////////////////////////////////////////////////////////////
// Mobile card layout — below 700px the plan tables swap for stackable tap
// cards (.table-scroll.hide-mobile / .plan-cards toggle in web.css), the
// pattern phone carriers use: big amount header, one tap to continue.
///////////////////////////////////////////////////////////////////////////////

function CardsSkeleton() {
  return <div className="cards-grid">{[1, 2, 3, 4].map((i) => <div key={i} className="pcard skeleton" />)}</div>;
}

// "Asia 6GB 15Days" -> "Asia": the data and days already have their own
// lines, so only the region part of a regional plan's name is worth showing.
function regionLabel(p) {
  const label = String(p.name || '')
    .replace(/\d+(?:\.\d+)?\s*(?:GB|MB)(?:\/Day)?/ig, '')
    .replace(/\d+\s*Days?/ig, '')
    .replace(/\s{2,}/g, ' ').trim();
  return label || p.name;
}

// Data, days, price — nothing else. The tap opens the detail modal, which
// carries the name, networks, and the buy action.
function PlanCard({ p, onOpen }) {
  return (
    <div
      className="pcard" role="button" tabIndex={0}
      onClick={() => onOpen(p)}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(p); }}
    >
      <span className="pcard-head">{p.data_label ? `${p.data_label}${p.is_daily ? '/day' : ''}` : p.name}</span>
      <span className="pcard-body">
        {p.is_regional && <span className="pcard-name">{regionLabel(p)}</span>}
        {/* daily cards skip the days line — their group header already says it */}
        {!p.is_daily && <span className="pcard-meta">{durationLabel(p)}</span>}
        <span className="pcard-price">
          {fmtPrice(p.price)}
          {p.is_daily && <em>/day</em>}
        </span>
      </span>
    </div>
  );
}

function EsimPlanCards({ products, shown, showSingle, query, filter, currency, onOpen }) {
  if (products == null) return <div className="plan-cards"><CardsSkeleton /></div>;
  // One summary line replaces the sortrow and the first group header: the
  // flag says the country, so "68 plans · prices in USD" is all that's left.
  const flag = query.country && /^[A-Z]{2}$/.test(query.country) ? flagEmoji(query.country) : '🌐';
  const groups = [];
  if (showSingle && shown.singlePkg.length) groups.push([null, shown.singlePkg]);
  if (showSingle && shown.singleDaily.length) groups.push(['Daily plans — choose the days', shown.singleDaily]);
  if (shown.regionalPkg.length) groups.push([query.country ? `Regional — also covers ${query.name}` : 'Regional & multi-country', shown.regionalPkg]);
  if (shown.regionalDaily.length) groups.push(['Regional daily plans', shown.regionalDaily]);
  return (
    <div className="plan-cards">
      <div className="cards-summary">{flag} <b>{shown.count} plan{shown.count === 1 ? '' : 's'}</b>&nbsp;· prices in {currency || 'USD'}</div>
      {groups.map(([title, list]) => (
        <React.Fragment key={title || 'packages'}>
          {title && <div className="cards-grp">{title}</div>}
          <div className="cards-grid">
            {list.map((p) => (
              <PlanCard key={p.slug} p={p} onOpen={onOpen} />
            ))}
          </div>
        </React.Fragment>
      ))}
      {shown.count === 0 && <div className="notice">No plans found{filter ? ` matching “${filter}”` : ''}.</div>}
    </div>
  );
}

export default function App() {
  const [me, setMe] = useState(undefined); // undefined = loading, null = guest

  // Session bootstrap. The request bypasses the HTTP cache: a Back from a
  // hosted payment page used to replay the pre-signup `currentUser: null`
  // (#305). A failed request never means "signed out" - it is retried once,
  // then the page falls back to guest only if it never saw a user (Checkout
  // re-asks the server before creating an account). A page shown again from
  // the back/forward cache, or a tab that becomes visible, re-checks too.
  useEffect(() => {
    let cancelled = false;
    let lastAt = 0;
    const onCheckout = () => window.location.pathname.startsWith('/checkout');
    const load = async (attempt) => {
      lastAt = Date.now();
      try {
        const r = await fetch('/api/auth/me', { cache: 'no-store', credentials: 'same-origin' });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (!cancelled) setMe((d && d.currentUser) || null);
      } catch (err) {
        if (cancelled) return;
        if (!attempt) {
          setTimeout(() => { if (!cancelled) load(1); }, 1500);
          return;
        }
        if (posthog.__loaded && onCheckout()) {
          posthog.capture('checkout_session_lost', { reason: 'me_fetch_failed', message: err.message || null });
        }
        setMe((cur) => (cur === undefined ? null : cur));
      }
    };
    load(0);
    const recheck = () => { if (Date.now() - lastAt > 2000) load(0); };
    const onPageShow = (e) => { if (e.persisted) recheck(); };
    const onVisible = () => { if (document.visibilityState === 'visible') recheck(); };
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Tie browser activity to the same distinct_id the server uses (user.id in
  // lib/events.coffee), so one person = one PostHog profile. On logout the
  // next page load sees a guest and resets, so a shared device doesn't merge
  // the next visitor into the previous account. A re-check that flips a user
  // to null mid-page is a lost session, not a logout: record it, keep the
  // identity (#305).
  const sawUser = useRef(false);
  useEffect(() => {
    if (!posthog.__loaded || me === undefined) return;
    if (me && me.id) {
      sawUser.current = true;
      if (posthog.get_distinct_id() !== String(me.id)) posthog.identify(String(me.id), { email: me.email });
    } else if (sawUser.current) {
      if (window.location.pathname.startsWith('/checkout')) {
        posthog.capture('checkout_session_lost', { reason: 'me_null_after_identify' });
      }
    } else if (posthog._isIdentified && posthog._isIdentified()) {
      posthog.reset();
    }
  }, [me && me.id]);

  const [currency, setCurrency] = useDisplayCurrency(me);
  const myCountries = useMyCountries(me);

  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  if (path.startsWith('/checkout')) {
    return <CheckoutPage me={me} onMe={setMe} currency={currency} onCurrency={setCurrency} />;
  }
  if (path.startsWith('/login')) {
    return <LoginPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path.startsWith('/signup')) {
    return <SignupPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path.startsWith('/reset-password')) {
    return <ResetPasswordPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (/^\/confirm-email-change\//.test(path)) {
    return <ConfirmEmailChangePage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (/^\/confirm-email\//.test(path)) {
    return <ConfirmEmailPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (/^\/receipt\//.test(path)) {
    return <ReceiptPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (/^\/esim\/topup\//.test(path)) {
    return <EsimTopupPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path.startsWith('/settings')) {
    return <SettingsPage me={me} onMe={setMe} currency={currency} onCurrency={setCurrency} />;
  }
  if (path === '/account/referrals' || path === '/referrals') {
    return <ReferralsPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path.startsWith('/top-me-up')) {
    return <TopMeUpPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (/^\/r\/[A-Za-z]{3}-[0-9A-Fa-f]{6,8}$/.test(path)) {
    return <TopupPayPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path.startsWith('/support')) {
    return <SupportPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path === '/contact') {
    return <ContactPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path.startsWith('/orders')) {
    return <OrdersPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path === '/wallet/deposit/usd') {
    return <DepositUSDPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path === '/wallet/deposit') {
    return <DepositTHBPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path === '/wallet') {
    return <WalletPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path.startsWith('/numbers')) {
    return <NumbersPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path === '/refund-policy' || path === '/refunds') {
    return <RefundPolicyPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path === '/terms') {
    return <TermsPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path === '/privacy') {
    return <PrivacyPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path === '/cookie-privacy') {
    return <CookiePolicyPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path === '/security') {
    return <SecurityPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path === '/about') {
    return <AboutPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path === '/agents') {
    return <AgentsPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path === '/lucky-number') {
    return <LuckyPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  // /uk: a market home selling everything in that market (markets.js).
  const market = marketForPath(path);
  if (market) {
    return <MarketHome market={market} me={me} currency={currency} onCurrency={setCurrency} />;
  }
  if (path === '/countries') {
    return <CountriesPage me={me} currency={currency} onCurrency={setCurrency} />;
  }
  // /gifts/<country> landing pages — same slug resolver as /topup and /esim.
  const giftsMatch = /^\/gifts\/([a-z0-9-]+)\/?$/.exec(path);
  if (giftsMatch) {
    const c = findBySlug(giftsMatch[1]);
    if (c) {
      return <GiftsPage me={me} currency={currency} onCurrency={setCurrency} myCountries={myCountries} landing={c} />;
    }
  }
  if (path === '/gifts') {
    return <GiftsPage me={me} currency={currency} onCurrency={setCurrency} myCountries={myCountries} />;
  }
  if (path.startsWith('/recharge')) {
    // The games tab moved to its own page — honour old links
    if (new URLSearchParams(window.location.search).get('tab') === 'games') {
      window.location.replace('/gifts');
      return null;
    }
    return <RechargePage me={me} currency={currency} onCurrency={setCurrency} myCountries={myCountries} />;
  }

  // Country landing pages are the main pages preset to a country — see
  // lib/countrySlugs.js for the slug set and Landing.jsx for the copy.
  // Optional 2nd segment = carrier slug (/topup/united-states/t-mobile). The
  // server already validated country+carrier before serving this bundle —
  // see lib/routes/new-home.js — so the client only needs to pass the raw
  // slug through as a pre-select hint.
  const topupMatch = /^\/topup\/([a-z0-9-]+)(?:\/([a-z0-9-]+))?\/?$/.exec(path);
  if (topupMatch) {
    const region = REGIONS[topupMatch[1]];
    if (region) {
      return <RegionLanding region={region} me={me} currency={currency} onCurrency={setCurrency} />;
    }
    const c = findBySlug(topupMatch[1]);
    if (c) {
      const landing = { ...c, ...(TOPUP_COPY[c.iso] || genericTopupCopy(c.name)) };
      return (
        <RechargePage
          me={me} currency={currency} onCurrency={setCurrency} landing={landing} myCountries={myCountries}
          carrierSlug={topupMatch[2] || null}
        />
      );
    }
  }
  // /esim/<country> landing pages, with optional pre-expanded size deep link
  // (/esim/thailand/3_30). /esim/topup/<id> is handled above.
  const esimMatch = /^\/esim\/([a-z0-9-]+)(?:\/([a-z0-9_-]+))?\/?$/.exec(path);
  if (esimMatch) {
    const c = findBySlug(esimMatch[1]);
    if (c) {
      return <EsimLanding me={me} currency={currency} onCurrency={setCurrency} landing={c} planSlug={esimMatch[2] || null} />;
    }
  }
  return <EsimHome me={me} currency={currency} onCurrency={setCurrency} myCountries={myCountries} />;
}
